"use client";

import type { SuperAccordionContent } from "agir-types";
import type { ReactNode } from "react";

import { Flex } from "@radix-ui/themes";
import clsx from "clsx";
import { useMemo } from "react";

import {
  TypoHeading,
  typoHeadingMargin,
} from "@/src/components/typography/TypoHeading";
import { CollapsibleAccordion } from "@/src/components/ui/Accordion/CollapsibleAccordion";
import { ButtonLink } from "@/src/components/ui/Button/ButtonLink";
import { StrapiRichText } from "@/src/infrastructure/strapi/StrapiRichText";

interface SuperAccordionProps {
  readonly path: string;
  readonly superAccordion: SuperAccordionContent;
}

export function SuperAccordion({
  path,
  superAccordion,
}: SuperAccordionProps): ReactNode {
  const hasMainContent: boolean = useMemo(
    () =>
      superAccordion.accordion.length > 0 ||
      superAccordion.mainContent.length > 0 ||
      superAccordion.link !== null,
    [
      superAccordion.accordion.length,
      superAccordion.mainContent.length,
      superAccordion.link,
    ],
  );

  return (
    <Flex direction="column" gap="4">
      <header>
        <TypoHeading className={typoHeadingMargin[2]} level={2}>
          {superAccordion.title}
        </TypoHeading>
        {superAccordion.description.length > 0 ? (
          <StrapiRichText
            content={superAccordion.description}
            headingLevel={3}
            path={path}
          />
        ) : null}
      </header>
      {hasMainContent ? (
        <div className={clsx("flex flex-col gap-4", "sm:flex-row sm:gap-10")}>
          {superAccordion.asideContent.length > 0 ? (
            <aside
              className={clsx(
                "shrink-0 grow basis-1/3",
                "[&>*:first-child]:mt-0",
                "[&>*:last-child]:mb-0",
              )}
            >
              <StrapiRichText
                content={superAccordion.asideContent}
                headingLevel={3}
                path={path}
              />
            </aside>
          ) : null}
          <div className="flex grow basis-2/3 flex-col gap-6">
            {superAccordion.mainContent.length > 0 ? (
              <div className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                <StrapiRichText
                  content={superAccordion.mainContent}
                  headingLevel={3}
                  path={path}
                />
              </div>
            ) : null}
            <CollapsibleAccordion
              items={superAccordion.accordion.map(
                ({ content, header, id }) => ({
                  content: (
                    <StrapiRichText
                      content={content}
                      headingLevel={4}
                      path={path}
                    />
                  ),
                  header,
                  id,
                }),
              )}
            />
            {superAccordion.link ? (
              <ButtonLink
                className={clsx("w-full", "sm:w-fit")}
                href={superAccordion.link.url}
                size="big"
              >
                {superAccordion.link.text}
              </ButtonLink>
            ) : null}
          </div>
        </div>
      ) : superAccordion.asideContent.length > 0 ? (
        <StrapiRichText
          content={superAccordion.asideContent}
          headingLevel={3}
          path={path}
        />
      ) : null}
    </Flex>
  );
}
